/**
 * Date plumbing between Studio and `<input type="datetime-local">`.
 *
 * Studio stores schedule fields as UTC ISO strings and also returns a pre-formatted
 * version for display. `datetime-local` speaks neither: it has no timezone at all, and
 * it wants exactly `YYYY-MM-DDTHH:mm`.
 *
 * The conversion is deliberately UTC on both sides rather than local time. Course dates
 * are an editorial decision made once and read by learners in every timezone, so an
 * author setting "1 September, 09:00" means 09:00 UTC — the same instant regardless of
 * where they happened to be sitting. Converting through local time would mean the same
 * course gained a different release date depending on who edited it last, which is the
 * kind of bug nobody finds until a cohort sees content a day early.
 */

/** ISO (or null) → the value a `datetime-local` input wants. Empty string means unset. */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}

/** A `datetime-local` value → ISO for Studio. Empty input means "unschedule", so null. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(`${value}:00Z`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Studio's default "unset" start date.
 *
 * A course with no release date is stored as the year 2040, not as null — that is how
 * the platform keeps unscheduled content out of learners' hands by default. Showing
 * "1 January 2040" to an author is meaningless, so it reads as unscheduled instead.
 */
const UNSET_YEAR = 2040;

export function isUnscheduled(iso: string | null): boolean {
  if (!iso) return true;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) || date.getUTCFullYear() >= UNSET_YEAR;
}
